<script setup lang="ts">
import { computed, ref } from 'vue';

import type {
  FormFlattenKey,
  FormFlattenValue,
  FormResult,
  FormSchema,
} from '@modules/forms/components/VForm/types';
import type { ISettingsAffiliateBannerForm } from '@modules/general-settings/submodules/affiliate-banner/types';
import VButton from '@components/buttons/VButton/VButton.vue';
import VCard from '@components/card/VCard/VCard.vue';
import VLoader from '@components/loader/VLoader.vue';
import AffiliateBannerDialog from '@modules/affiliate-banner-view/components/AffiliateBannerDialog/AffiliateBannerDialog.vue';
import ServerPayloadSizeError from '@modules/core/errors/ServerPayloadSizeError';
import VForm from '@modules/forms/components/VForm/VForm.vue';
import { EFormInputTypes } from '@modules/forms/enums';
import { TRANSLATION_GROUP_GENERAL_SETTINGS } from '@modules/general-settings/constants';
import {
  ADMIN_GENERAL_SETTINGS_AFFILIATE_BANNER_PERMISSIONS,
  TRANSLATION_GROUP_GENERAL_SETTINGS_AFFILIATE_BANNER,
} from '@modules/general-settings/submodules/affiliate-banner/constants';
import { ESettingsAffiliateBannerField } from '@modules/general-settings/submodules/affiliate-banner/enums';
import useSettingsAffiliateBannerSaveMutation
  from '@modules/general-settings/submodules/affiliate-banner/mutations/useSettingsAffiliateBannerSaveMutation';
import useSettingsAffiliateBannerQuery
  from '@modules/general-settings/submodules/affiliate-banner/queries/useSettingsAffiliateBannerQuery';
import { useI18n } from '@modules/i18n/composables';
import { isUndefined } from '@utils/guards';
import { watchImmediate } from '@vueuse/core';

const { getTranslation, getTPath } = useI18n();

const { data, isLoading } = useSettingsAffiliateBannerQuery();
const {
  mutateAsync: uploadBanner,
  isLoading: isLoadingSubmission,
  error,
  errors,
  clearError,
  data: savedData,
} = useSettingsAffiliateBannerSaveMutation();

const isPreviewDialogVisible = ref(false);

const formPayload = ref<ISettingsAffiliateBannerForm>();

const customErrors = computed(() => {
  let errorsC = {};
  if (error.value instanceof ServerPayloadSizeError) {
    errorsC = {
      [ESettingsAffiliateBannerField.IMAGE]: [getTranslation(TRANSLATION_GROUP_GENERAL_SETTINGS_AFFILIATE_BANNER, 'INVALID_FILE_SIZE').value],
    };
  }
  return { ...errors.value, ...errorsC };
});

const previewImage = computed(() => {
  const file = formPayload.value?.image;
  const link = data.value?.banner.image;
  if (file) {
    return URL.createObjectURL(file);
  }

  return link;
});

const formSchema = computed<FormSchema<ISettingsAffiliateBannerForm>>(() => ({
  [ESettingsAffiliateBannerField.IMAGE]: {
    type: EFormInputTypes.SINGLE_FILE,
    label: getTranslation(TRANSLATION_GROUP_GENERAL_SETTINGS_AFFILIATE_BANNER, 'CHOOSE_BANNER').value,
    disabled: isLoading.value,
    dragAndDropLabel: getTranslation(TRANSLATION_GROUP_GENERAL_SETTINGS_AFFILIATE_BANNER, 'DROP_BANNER_HERE', { format: 'png, jpg, gif' }).value,
    accept: 'image/png, image/jpeg, image/gif',
    format: 'png, jpg, gif',
    maxSizeBytes: 1_048_576, // 1 MB
    invalidTypeLabel: getTPath(TRANSLATION_GROUP_GENERAL_SETTINGS_AFFILIATE_BANNER, 'INVALID_FILE_FORMAT'),
    invalidSizeLabel: getTPath(TRANSLATION_GROUP_GENERAL_SETTINGS_AFFILIATE_BANNER, 'INVALID_FILE_SIZE'),
    iconType: 'image',
    value: formPayload.value?.image,
  },
  [ESettingsAffiliateBannerField.LINK]: {
    type: EFormInputTypes.TEXT,
    label: getTranslation(TRANSLATION_GROUP_GENERAL_SETTINGS_AFFILIATE_BANNER, 'LINK').value,
    disabled: isLoading.value,
    value: formPayload.value?.link,
  },
  [ESettingsAffiliateBannerField.ENABLED]: {
    type: EFormInputTypes.CHECKBOX,
    label: getTranslation(TRANSLATION_GROUP_GENERAL_SETTINGS_AFFILIATE_BANNER, 'ENABLED').value,
    value: formPayload.value?.enabled,
  },
}));

function handleChange<
  T extends ISettingsAffiliateBannerForm,
  K extends FormFlattenKey<T>,
>(key: K, value: FormFlattenValue<T, K> | undefined, result: FormResult<T>): void {
  formPayload.value = result.values;
}

watchImmediate(data, (newValue) => {
  if (newValue) {
    const { image, ...rest } = newValue.banner;
    formPayload.value = {
      image: undefined,
      ...rest,
    };
  }
});

async function handleSubmit(payload: ISettingsAffiliateBannerForm): Promise<void> {
  formPayload.value = payload;
  const formData = new FormData();

  for (const [name, value] of Object.entries(payload)) {
    if (!isUndefined(value)) {
      formData.append(name, value);
    }
  }

  await uploadBanner(formData);
  if (savedData.value?.banner) {
    const { image, ...rest } = savedData.value.banner;
    formPayload.value = {
      image: undefined,
      ...rest,
    };
  }
}

function onOpenDialog(): void {
  isPreviewDialogVisible.value = true;
}
</script>

<template>
  <VCard v-data-qa-auto-id="'AdminGeneralSettingsAffiliateBannerPage'">
    <VLoader :loading="isLoading">
      <VForm
        :schema="formSchema"
        :is-loading="isLoadingSubmission"
        :submit-btn-label="$getTranslation(TRANSLATION_GROUP_GENERAL_SETTINGS, 'SAVE')"
        :custom-errors="customErrors"
        :readonly="!$hasPermission(ADMIN_GENERAL_SETTINGS_AFFILIATE_BANNER_PERMISSIONS.BANNERSETTINGS_EDIT)"
        @change="handleChange"
        @submit="handleSubmit"
        @interacted="clearError"
      >
        <template #secondaryBtn>
          <VButton
            :label="$getTranslation(TRANSLATION_GROUP_GENERAL_SETTINGS_AFFILIATE_BANNER, 'PREVIEW_BANNER_BTN')"
            outlined
            @click="onOpenDialog"
          />
        </template>
      </VForm>
    </VLoader>
    <AffiliateBannerDialog
      v-model:visible="isPreviewDialogVisible"
      :image-url="previewImage"
      :redirect-url="formPayload?.link"
      dismissable-mask
      close-on-escape
    />
  </VCard>
</template>
